import { Action, handleActions } from 'redux-actions'
import {
  getCompanySsoConfigs,
  getCompanySsoConfigsSuccess,
  getCompanySsoConfigsFail,
  getCompanySsoConfig,
  getCompanySsoConfigSuccess,
  getCompanySsoConfigFail,
  createCompanySsoConfig,
  createCompanySsoConfigSuccess,
  createCompanySsoConfigFail,
  updateCompanySsoConfig,
  updateCompanySsoConfigSuccess,
  updateCompanySsoConfigFail,
  deleteCompanySsoConfig,
  deleteCompanySsoConfigSuccess,
  deleteCompanySsoConfigFail,
  setSelectedSsoConfig,
  enableScim,
  enableScimSuccess,
  enableScimFail,
  disableScim,
  disableScimSuccess,
  disableScimFail,
  regenerateScimToken,
  regenerateScimTokenSuccess,
  regenerateScimTokenFail,
  getCompanySsoSettings,
  getCompanySsoSettingsSuccess,
  getCompanySsoSettingsFail,
  updateCompanySsoSettings,
  updateCompanySsoSettingsSuccess,
  updateCompanySsoSettingsFail,
} from './SsoAdminActions'
import { setCompanyAdminSelectedCompanyId } from '../../CompanyAdmin/store/CompanyAdminActions'
import { GetCompanySsoConfig$Response, SsoConfigListItem, ScimConfigDto } from '../../../services/api.types'

/**
 * SSO Admin state.
 * Supports 1:N SSO configs per company with list + detail views.
 */
class State {
  // List of all SSO configs for the selected company
  configs: SsoConfigListItem[] = []
  // Currently selected config for editing (null = list view)
  selectedConfig: GetCompanySsoConfig$Response | null = null
  loading: boolean = false
  saving: boolean = false
  deleting: boolean = false
  // ID returned after creating a new config (used by wizard flow)
  createdConfigId: string | null = null
  // SCIM
  scimConfig: ScimConfigDto | null = null
  scimTokenVisible: string | null = null
  scimSaving: boolean = false
  // Company-level SSO settings
  ssoRequired: boolean = false
  ssoSettingsLoading: boolean = false
  ssoSettingsUpdating: boolean = false
}

export const ssoAdmin = handleActions<State, any>(
  {
    // --- Get SSO Configs (list) ---
    [String(getCompanySsoConfigs)]: (state: State): State => ({
      ...state,
      loading: true,
    }),
    [String(getCompanySsoConfigsSuccess)]: (state: State, action: Action<any>): State => ({
      ...state,
      loading: false,
      configs: action.payload?.configs || [],
    }),
    [String(getCompanySsoConfigsFail)]: (state: State): State => ({
      ...state,
      loading: false,
      configs: [],
    }),

    // --- Get Single SSO Config (detail) ---
    [String(getCompanySsoConfig)]: (state: State): State => ({
      ...state,
      loading: true,
    }),
    [String(getCompanySsoConfigSuccess)]: (state: State, action: Action<any>): State => ({
      ...state,
      loading: false,
      selectedConfig: action.payload || null,
      scimConfig: action.payload?.scimConfig || null,
    }),
    [String(getCompanySsoConfigFail)]: (state: State): State => ({
      ...state,
      loading: false,
      selectedConfig: null,
    }),

    // --- Create SSO Config ---
    [String(createCompanySsoConfig)]: (state: State): State => ({
      ...state,
      saving: true,
    }),
    [String(createCompanySsoConfigSuccess)]: (state: State, action: Action<any>): State => ({
      ...state,
      saving: false,
      createdConfigId: action.payload?.configId || null,
    }),
    [String(createCompanySsoConfigFail)]: (state: State): State => ({
      ...state,
      saving: false,
    }),

    // --- Update SSO Config ---
    [String(updateCompanySsoConfig)]: (state: State): State => ({
      ...state,
      saving: true,
    }),
    [String(updateCompanySsoConfigSuccess)]: (state: State): State => ({
      ...state,
      saving: false,
    }),
    [String(updateCompanySsoConfigFail)]: (state: State): State => ({
      ...state,
      saving: false,
    }),

    // --- Delete SSO Config ---
    [String(deleteCompanySsoConfig)]: (state: State): State => ({
      ...state,
      deleting: true,
    }),
    [String(deleteCompanySsoConfigSuccess)]: (state: State): State => ({
      ...state,
      deleting: false,
      selectedConfig: null,
      scimConfig: null,
    }),
    [String(deleteCompanySsoConfigFail)]: (state: State): State => ({
      ...state,
      deleting: false,
    }),

    // --- Selected Company (reset SSO-specific state when company changes) ---
    [String(setCompanyAdminSelectedCompanyId)]: (state: State): State => ({
      ...state,
      selectedConfig: null,
      createdConfigId: null,
      scimConfig: null,
      scimTokenVisible: null,
      ssoRequired: false,
    }),

    // --- Selected Config (navigate to detail / back to list) ---
    [String(setSelectedSsoConfig)]: (state: State, action: Action<any>): State => ({
      ...state,
      selectedConfig: action.payload || null,
      createdConfigId: null,
      scimConfig: null,
      scimTokenVisible: null,
    }),

    // --- SCIM ---
    [String(enableScim)]: (state: State): State => ({
      ...state,
      scimSaving: true,
    }),
    [String(enableScimSuccess)]: (state: State, action: Action<any>): State => ({
      ...state,
      scimSaving: false,
      scimConfig: action.payload?.scimConfig || null,
      scimTokenVisible: action.payload?.bearerToken || null,
    }),
    [String(enableScimFail)]: (state: State): State => ({
      ...state,
      scimSaving: false,
    }),

    [String(disableScim)]: (state: State): State => ({
      ...state,
      scimSaving: true,
    }),
    [String(disableScimSuccess)]: (state: State): State => ({
      ...state,
      scimSaving: false,
      scimConfig: null,
      scimTokenVisible: null,
    }),
    [String(disableScimFail)]: (state: State): State => ({
      ...state,
      scimSaving: false,
    }),

    [String(regenerateScimToken)]: (state: State): State => ({
      ...state,
      scimSaving: true,
    }),
    [String(regenerateScimTokenSuccess)]: (state: State, action: Action<any>): State => ({
      ...state,
      scimSaving: false,
      scimTokenVisible: action.payload?.bearerToken || null,
    }),
    [String(regenerateScimTokenFail)]: (state: State): State => ({
      ...state,
      scimSaving: false,
    }),

    // --- Company-level SSO Settings ---
    [String(getCompanySsoSettings)]: (state: State): State => ({
      ...state,
      ssoSettingsLoading: true,
    }),
    [String(getCompanySsoSettingsSuccess)]: (state: State, action: Action<any>): State => ({
      ...state,
      ssoSettingsLoading: false,
      ssoRequired: action.payload?.ssoRequired || false,
    }),
    [String(getCompanySsoSettingsFail)]: (state: State): State => ({
      ...state,
      ssoSettingsLoading: false,
    }),

    [String(updateCompanySsoSettings)]: (state: State): State => ({
      ...state,
      ssoSettingsUpdating: true,
    }),
    [String(updateCompanySsoSettingsSuccess)]: (state: State, action: Action<any>): State => ({
      ...state,
      ssoSettingsUpdating: false,
      ssoRequired: action.payload?.ssoRequired ?? state.ssoRequired,
    }),
    [String(updateCompanySsoSettingsFail)]: (state: State): State => ({
      ...state,
      ssoSettingsUpdating: false,
    }),
  },
  new State(),
)

export { State as SsoAdminState }
